import React, { createContext, useState, useContext, ReactNode, useEffect, useRef } from 'react';
import { 
    collection, 
    doc, 
    addDoc, 
    updateDoc, 
    getDoc,
    getDocs, 
    onSnapshot, 
    query, 
    orderBy,
    where,
    Timestamp,
    increment,
    deleteField
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { onAuthStateChanged, signInAnonymously } from 'firebase/auth';
import { db, storage, primaryAuth } from '../firebase';
import { createAuthAccount, finishAccountCreation, getSecondaryDb, writeAuthMap, writeLoginAlias, syntheticEmailForUsername, isEmail } from '../utils/authAccounts';
import { doc as firestoreDoc, setDoc } from 'firebase/firestore';
import { User, UserRole, ShipmentPost, PostStatus, ReservationRequest, RequestStatus, BalanceTransaction, TransactionType, Seller, Product, LostPackageReport, LostPackageReportStatus, Office, WithdrawalRequest, WithdrawalRequestStatus, PendingTransfer, SellerPayment, UserApprovalRequest, UserApprovalRequestStatus, ShortageDelivery, ShortageDeliveryStatus, NotificationType, Notification } from '../types';
import { hashPassword } from '../utils/passwordHash';
import { translations } from '../constants';
import { createNotification, sendNotificationToOffice } from '../utils/notifications';

interface DataContextType {
    users: User[];
    setUsers: React.Dispatch<React.SetStateAction<User[]>>;
    posts: ShipmentPost[];
    setPosts: React.Dispatch<React.SetStateAction<ShipmentPost[]>>;
    requests: ReservationRequest[];
    setRequests: React.Dispatch<React.SetStateAction<ReservationRequest[]>>;
    transactions: BalanceTransaction[];
    setTransactions: React.Dispatch<React.SetStateAction<BalanceTransaction[]>>;
    sellers: Seller[];
    setSellers: React.Dispatch<React.SetStateAction<Seller[]>>;
    products: Product[];
    setProducts: React.Dispatch<React.SetStateAction<Product[]>>;
    sellerPayments: SellerPayment[];
    setSellerPayments: React.Dispatch<React.SetStateAction<SellerPayment[]>>;
    lostPackageReports: LostPackageReport[];
    setLostPackageReports: React.Dispatch<React.SetStateAction<LostPackageReport[]>>;
    offices: Office[];
    setOffices: React.Dispatch<React.SetStateAction<Office[]>>;
    withdrawalRequests: WithdrawalRequest[];
    setWithdrawalRequests: React.Dispatch<React.SetStateAction<WithdrawalRequest[]>>;
    pendingTransfers: PendingTransfer[];
    setPendingTransfers: React.Dispatch<React.SetStateAction<PendingTransfer[]>>;
    userApprovalRequests: UserApprovalRequest[];
    setUserApprovalRequests: React.Dispatch<React.SetStateAction<UserApprovalRequest[]>>;
    shortageDeliveries: ShortageDelivery[];
    setShortageDeliveries: React.Dispatch<React.SetStateAction<ShortageDelivery[]>>;
    notifications: Notification[];
    setNotifications: React.Dispatch<React.SetStateAction<Notification[]>>;
    loading: boolean;
    addRequest: (request: Omit<ReservationRequest, 'id' | 'createdAt' | 'status'>) => Promise<void>;
    updateRequestStatus: (requestId: string, status: RequestStatus, qrData?: string, workerId?: string) => Promise<void>;
    addPost: (post: Omit<ShipmentPost, 'id' | 'createdAt'>) => Promise<void>;
    updatePost: (post: ShipmentPost) => Promise<void>;
    markPackageDelivered: (requestId: string, workerId?: string, receivedKg?: number, shortageAction?: 'deduct' | 'cash') => Promise<void>;
    addSeller: (seller: Omit<Seller, 'id' | 'createdAt'>) => Promise<void>;
    updateSeller: (seller: Seller) => Promise<void>;
    addProduct: (product: Omit<Product, 'id' | 'createdAt'>) => Promise<void>;
    updateProduct: (product: Product) => Promise<void>;
    deleteSeller: (sellerId: string) => Promise<void>;
    deleteProduct: (productId: string) => Promise<void>;
    deletePost: (postId: string) => Promise<void>;
    checkAndArchiveAllPosts: () => Promise<void>;
    transferPackage: (requestId: string, newUserId: string, giverId?: string, viaTransferId?: string) => Promise<void>;
    createPendingTransfer: (requestId: string, giverId: string, receiverId: string) => Promise<string>;
    confirmPendingTransfer: (transferId: string, userId: string) => Promise<void>;
    cancelPendingTransfer: (transferId: string) => Promise<void>;
    reportLostPackage: (requestId: string, description: string, file?: File) => Promise<void>;
    handleLostPackageReport: (reportId: string, status: LostPackageReportStatus, adminUserId: string, adminNotes?: string) => Promise<void>;
    // Withdrawal Requests
    createWithdrawalRequest: (userId: string, officeId: string, amount: number, currency?: string) => Promise<void>;
    handleWithdrawalRequest: (requestId: string, status: WithdrawalRequestStatus, adminUserId: string, adminNotes?: string) => Promise<void>;
    // Office and Worker Management
    addOffice: (office: Omit<Office, 'id' | 'createdAt'>) => Promise<string>; // Returns office ID
    updateOffice: (office: Office) => Promise<void>;
    deleteOffice: (officeId: string) => Promise<void>;
    addUser: (user: Omit<User, 'id'>) => Promise<void>;
    updateUser: (user: User) => Promise<void>;
    deleteUser: (userId: string) => Promise<void>;
    // Seller Payments
    addSellerPayment: (payment: Omit<SellerPayment, 'id' | 'createdAt'>) => Promise<void>;
    // User Approval Requests
    createUserApprovalRequest: (userId: string, officeId: string, message?: string, file?: File) => Promise<void>;
    handleUserApprovalRequest: (requestId: string, status: UserApprovalRequestStatus, reviewerId: string, adminNotes?: string) => Promise<void>;
    // Shortage Delivery Management
    handleShortageDeliveryAction: (shortageId: string, action: 'deduct' | 'hold', officeUserId: string) => Promise<void>;
    deliverMissingWeight: (shortageId: string, deliveredKg: number, workerId?: string) => Promise<void>;
    handleShortageFinalAction: (shortageId: string, action: 'deduct' | 'pay', officeUserId: string, userApprovalId?: string) => Promise<void>;
    // Manual Package Creation
    createManualPackage: (postId: string, name: string, surname: string, phone: string, requestedKg: number, workerId?: string, bagNumber?: string) => Promise<{ requestId: string; qrCodeData: string }>;
    // Notifications
    markNotificationAsRead: (notificationId: string) => Promise<void>;
}

const DataContext = createContext<DataContextType | undefined>(undefined);

export const DataProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [users, setUsers] = useState<User[]>([]);
    const [posts, setPosts] = useState<ShipmentPost[]>([]);
    const [requests, setRequests] = useState<ReservationRequest[]>([]);
    const [transactions, setTransactions] = useState<BalanceTransaction[]>([]);
    const [sellers, setSellers] = useState<Seller[]>([]);
    const [products, setProducts] = useState<Product[]>([]);
    const [sellerPayments, setSellerPayments] = useState<SellerPayment[]>([]);
    const [lostPackageReports, setLostPackageReports] = useState<LostPackageReport[]>([]);
    const [offices, setOffices] = useState<Office[]>([]);
    const [withdrawalRequests, setWithdrawalRequests] = useState<WithdrawalRequest[]>([]);
    const [pendingTransfers, setPendingTransfers] = useState<PendingTransfer[]>([]);
    const [userApprovalRequests, setUserApprovalRequests] = useState<UserApprovalRequest[]>([]);
    const [shortageDeliveries, setShortageDeliveries] = useState<ShortageDelivery[]>([]);
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    
    // Global timeout to force loading to false
    useEffect(() => {
        const forceLoadingFalse = () => {
            console.warn('[DATA] Received force-loading-false event, setting loading to false');
            setLoading(false);
        };
        window.addEventListener('force-loading-false', forceLoadingFalse);
        
        // Also set a local timeout as backup
        const timeoutId = setTimeout(() => {
            console.warn('[DATA] DataContext timeout after 8 seconds, setting loading to false');
            setLoading(false);
        }, 8000);
        
        return () => {
            window.removeEventListener('force-loading-false', forceLoadingFalse);
            clearTimeout(timeoutId);
        };
    }, []);

    // ----- Firebase Auth gate for Firestore listeners -----
    // Under locked-down security rules every read needs an auth token, so the
    // listeners below must attach only after the auth state has settled (they
    // would otherwise fail with permission-denied and never recover). Visitors
    // (e.g. someone scanning a package QR code) are signed in anonymously so
    // the public package page keeps working. `authEpoch` changes whenever the
    // signed-in identity changes, which re-attaches all listeners with the new
    // permissions.
    const [authEpoch, setAuthEpoch] = useState<number | null>(null);
    useEffect(() => {
        if (!primaryAuth) {
            setAuthEpoch(0); // no auth available; attach listeners as before
            return;
        }
        const unsubscribe = onAuthStateChanged(primaryAuth, (fbUser) => {
            if (!fbUser) {
                signInAnonymously(primaryAuth!).catch(err => {
                    console.warn('[DATA] Anonymous sign-in failed (enable the Anonymous provider in the Firebase console):', err?.code || err);
                    // Attach listeners anyway — this keeps the app working
                    // while the security rules are still open.
                    setAuthEpoch(prev => (prev ?? 0) + 1);
                });
                return; // wait for the anonymous sign-in to complete
            }
            setAuthEpoch(prev => (prev ?? 0) + 1);
        });
        return () => unsubscribe();
    }, []);

    // Helper function to check and archive post if stock is depleted
    const checkAndArchivePostIfStockDepleted = async (postId: string) => {
        try {
            // Fetch latest post data from Firestore
            const postRef = doc(db, 'posts', postId);
            const postDoc = await getDoc(postRef);
            
            if (!postDoc.exists()) {
                console.log(`Post ${postId} not found in Firestore`);
                return; // Post not found
            }
            
            const postData = postDoc.data();
            if (postData.archived) {
                console.log(`Post ${postId} already archived`);
                return; // Post already archived
            }
            
            const totalKg = postData.totalKg || 0;
            console.log(`Checking post ${postId}: totalKg=${totalKg}`);
            
            // Fetch all requests for this post from Firestore
            const requestsQuery = query(
                collection(db, 'requests'),
                where('postId', '==', postId)
            );
            const requestsSnapshot = await getDocs(requestsQuery);
            const postRequests = requestsSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            } as ReservationRequest));
            
            console.log(`Post ${postId}: Found ${postRequests.length} requests`);
            
            // Calculate reserved stock (Approved, InTransit, Delivered)
            // Handle both enum and string status values
            const reservedStock = postRequests
                .filter(r => {
                    const status = r.status as string;
                    return status === RequestStatus.Approved || 
                           status === RequestStatus.InTransit || 
                           status === RequestStatus.Delivered ||
                           status === 'Approved' ||
                           status === 'InTransit' ||
                           status === 'Delivered';
                })
                .reduce((sum, r) => sum + (r.requestedKg || 0), 0);
            
            console.log(`Post ${postId}: reservedStock=${reservedStock}`);
            
            // Fetch approved lost package reports from Firestore
            const lostReportsQuery = query(
                collection(db, 'lostPackageReports'),
                where('status', '==', LostPackageReportStatus.Approved)
            );
            const lostReportsSnapshot = await getDocs(lostReportsQuery);
            const approvedLostReports = lostReportsSnapshot.docs
                .map(doc => ({ id: doc.id, ...doc.data() } as LostPackageReport))
                .filter(report => {
                    // Check if the report's request belongs to this post
                    return postRequests.some(req => req.id === report.requestId);
                });
            
            const lostRequestIds = new Set(approvedLostReports.map(r => r.requestId));
            const lostStock = postRequests
                .filter(r => lostRequestIds.has(r.id))
                .reduce((sum, r) => sum + (r.requestedKg || 0), 0);
            
            console.log(`Post ${postId}: lostStock=${lostStock}`);
            
            // Calculate remaining stock
            // For per-piece products, we don't subtract lost stock from remaining (lost is already counted in reserved)
            // But we need to check if all stock is reserved (including lost)
            const remainingStock = totalKg - reservedStock;
            
            console.log(`Post ${postId}: remainingStock=${remainingStock} (totalKg=${totalKg}, reservedStock=${reservedStock}, lostStock=${lostStock})`);
            
            // If stock is depleted (<= 0), archive the post
            // Stock is depleted when remainingStock <= 0 (all stock is reserved/delivered)
            if (remainingStock <= 0 && totalKg > 0) {
                console.log(`Archiving post ${postId} - stock depleted`);
                await updateDoc(postRef, {
                    archived: true,
                    archivedAt: Timestamp.now()
                });
                console.log(`✅ Post ${postId} automatically archived - stock depleted (remaining: ${remainingStock})`);
            } else {
                console.log(`Post ${postId} still has stock (${remainingStock}), not archiving`);
            }
        } catch (error) {
            console.error(`❌ Error checking and archiving post ${postId}:`, error);
            // Don't throw - this is a background check
        }
    };

    // Fetch and listen to users collection
    useEffect(() => {
        if (authEpoch === null) return; // wait for auth to settle
        console.log('[DATA] Setting up users listener...');
        
        // Check if db is ready
        if (!db) {
            console.warn('[DATA] Firestore db not ready, will retry users listener setup');
            // Set loading to false so app can render
            setLoading(false);
            
            // Retry after a delay
            const retryTimeout = setTimeout(() => {
                if (db) {
                    console.log('[DATA] Retrying users listener setup...');
                    const unsubscribe = onSnapshot(
                        collection(db, 'users'),
                        (snapshot) => {
                            console.log('[DATA] Users snapshot received, count:', snapshot.docs.length);
                            const usersData: User[] = snapshot.docs
                                .map(doc => ({
                                    id: doc.id,
                                    ...doc.data()
                                } as User));
                            // Don't filter archived users here - let components filter based on their needs
                            setUsers(usersData);
                            setLoading(false);
                        },
                        (error) => {
                            console.error('[DATA] Error fetching users:', error);
                            setLoading(false);
                        }
                    );
                    return () => unsubscribe();
                }
            }, 1000);
            return () => clearTimeout(retryTimeout);
        }
        
        const unsubscribe = onSnapshot(
            collection(db, 'users'),
            (snapshot) => {
                console.log('[DATA] Users snapshot received, count:', snapshot.docs.length);
                const usersData: User[] = snapshot.docs
                    .map(doc => ({
                        id: doc.id,
                        ...doc.data()
                    } as User));
                // Don't filter archived users here - let components filter based on their needs
                setUsers(usersData);
                setLoading(false);
            },
            (error) => {
                console.error('[DATA] Error fetching users:', error);
                setLoading(false);
            }
        );
        return () => unsubscribe();
    }, [authEpoch]);

    // Track if we've done the initial stock check
    const hasCheckedInitialLoad = useRef(false);

    // Fetch and listen to posts collection
    useEffect(() => {
        if (authEpoch === null) return; // wait for auth to settle
        console.log('[DATA] Setting up posts listener...');
        
        // Check if db is ready
        if (!db) {
            console.warn('[DATA] Firestore db not ready, will retry posts listener setup');
            // Retry after a delay
            const retryTimeout = setTimeout(() => {
                if (db) {
                    console.log('[DATA] Retrying posts listener setup...');
                    const q = query(collection(db, 'posts'), orderBy('createdAt', 'desc'));
                    const unsubscribe = onSnapshot(
                        q,
                        async (snapshot) => {
                            console.log('[DATA] Posts snapshot received, count:', snapshot.docs.length);
                            const postsData: ShipmentPost[] = snapshot.docs
                                .map(doc => {
                                    const data = doc.data();
                                    return {
                                        id: doc.id,
                                        ...data,
                                        createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt || new Date().toISOString(),
                                        archived: data.archived || false, // Ensure archived field is set
                                        archivedAt: data.archivedAt?.toDate?.()?.toISOString() || data.archivedAt || undefined
                                    } as ShipmentPost;
                                });
                            // Don't filter here - let components filter based on their needs
                            setPosts(postsData);
                            
                            // Check and archive posts with depleted stock only on initial load
                            // This handles cases where posts were already depleted before the auto-archive feature was added
                            if (!hasCheckedInitialLoad.current) {
                                hasCheckedInitialLoad.current = true;
                                const activePosts = postsData.filter(p => !p.archived);
                                // Check posts in batches to avoid overwhelming Firestore
                                for (const post of activePosts) {
                                    await checkAndArchivePostIfStockDepleted(post.id);
                                }
                            }
                        },
                        (error) => {
                            console.error('[DATA] Error fetching posts:', error);
                        }
                    );
                    return () => unsubscribe();
                }
            }, 1000);
            return () => clearTimeout(retryTimeout);
        }
        
        const q = query(collection(db, 'posts'), orderBy('createdAt', 'desc'));
        const unsubscribe = onSnapshot(
            q,
            async (snapshot) => {
                console.log('[DATA] Posts snapshot received, count:', snapshot.docs.length);
                const postsData: ShipmentPost[] = snapshot.docs
                    .map(doc => {
                        const data = doc.data();
                        return {
                            id: doc.id,
                            ...data,
                            createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt || new Date().toISOString(),
                            archived: data.archived || false, // Ensure archived field is set
                            archivedAt: data.archivedAt?.toDate?.()?.toISOString() || data.archivedAt || undefined
                        } as ShipmentPost;
                    });
                // Don't filter here - let components filter based on their needs
                setPosts(postsData);
                
                // Check and archive posts with depleted stock only on initial load
                // This handles cases where posts were already depleted before the auto-archive feature was added
                if (!hasCheckedInitialLoad.current) {
                    hasCheckedInitialLoad.current = true;
                    const activePosts = postsData.filter(p => !p.archived);
                    // Check posts in batches to avoid overwhelming Firestore
                    for (const post of activePosts) {
                        await checkAndArchivePostIfStockDepleted(post.id);
                    }
                }
            },
            (error) => {
                console.error('[DATA] Error fetching posts:', error);
            }
        );
        return () => unsubscribe();
    }, [authEpoch]);

    // Fetch and listen to requests collection
    useEffect(() => {
        if (authEpoch === null) return; // wait for auth to settle
        const q = query(collection(db, 'requests'), orderBy('createdAt', 'desc'));
        const unsubscribe = onSnapshot(
            q,
            async (snapshot) => {
                const requestsData: ReservationRequest[] = snapshot.docs
                    .map(doc => {
                        const data = doc.data();
                        // Parse statusHistory if it exists
                        const statusHistory = data.statusHistory?.map((entry: any) => ({
                            status: entry.status,
                            timestamp: entry.timestamp?.toDate?.()?.toISOString() || entry.timestamp || new Date().toISOString()
                        })) || [];
                        
                        return {
                            id: doc.id,
                            ...data,
                            createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt || new Date().toISOString(),
                            statusHistory
                        } as ReservationRequest;
                    })
                    .filter(request => !request.archived); // Filter out archived requests
                setRequests(requestsData);
                
                // Check all active posts when requests change (to catch stock depletion)
                const activePosts = posts.filter(p => !p.archived);
                const affectedPostIds = new Set(requestsData.map(r => r.postId));
                for (const post of activePosts) {
                    if (affectedPostIds.has(post.id)) {
                        await checkAndArchivePostIfStockDepleted(post.id);
                    }
                }
            },
            (error) => {
                console.error('Error fetching requests:', error);
            }
        );
        return () => unsubscribe();
    }, [posts, authEpoch]);

    // Fetch and listen to transactions collection
    useEffect(() => {
        if (authEpoch === null) return; // wait for auth to settle
        const q = query(collection(db, 'transactions'), orderBy('date', 'desc'));
        const unsubscribe = onSnapshot(
            q,
            (snapshot) => {
                const transactionsData: BalanceTransaction[] = snapshot.docs
                    .map(doc => {
                        const data = doc.data();
                        return {
                            id: doc.id,
                            ...data,
                            date: data.date?.toDate?.()?.toISOString() || data.date || new Date().toISOString()
                        } as BalanceTransaction;
                    })
                    .filter(transaction => !transaction.archived); // Filter out archived transactions
                setTransactions(transactionsData);
            },
            (error) => {
                console.error('Error fetching transactions:', error);
            }
        );
        return () => unsubscribe();
    }, [authEpoch]);

    // Fetch and listen to sellers collection
    useEffect(() => {
        if (authEpoch === null) return; // wait for auth to settle
        const q = query(collection(db, 'sellers'), orderBy('createdAt', 'desc'));
        const unsubscribe = onSnapshot(
            q,
            (snapshot) => {
                const sellersData: Seller[] = snapshot.docs
                    .map(doc => {
                        const data = doc.data();
                        return {
                            id: doc.id,
                            ...data,
                            createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt || new Date().toISOString()
                        } as Seller;
                    })
                    .filter(seller => !seller.archived); // Filter out archived sellers
                setSellers(sellersData);
            },
            (error) => {
                console.error('Error fetching sellers:', error);
            }
        );
        return () => unsubscribe();
    }, [authEpoch]);

    // Fetch and listen to products collection
    useEffect(() => {
        if (authEpoch === null) return; // wait for auth to settle
        const q = query(collection(db, 'products'), orderBy('createdAt', 'desc'));
        const unsubscribe = onSnapshot(
            q,
            (snapshot) => {
                const productsData: Product[] = snapshot.docs
                    .map(doc => {
                        const data = doc.data();
                        return {
                            id: doc.id,
                            ...data,
                            archived: data.archived || false, // Ensure archived field is set
                            archivedAt: data.archivedAt?.toDate?.()?.toISOString() || data.archivedAt || undefined,
                            createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt || new Date().toISOString()
                        } as Product;
                    });
                // Don't filter out archived products - let components handle filtering based on their needs
                setProducts(productsData);
            },
            (error) => {
                console.error('Error fetching products:', error);
            }
        );
        return () => unsubscribe();
    }, [authEpoch]);

    // Fetch and listen to sellerPayments collection
    useEffect(() => {
        if (authEpoch === null) return; // wait for auth to settle
        const q = query(collection(db, 'sellerPayments'), orderBy('date', 'desc'));
        const unsubscribe = onSnapshot(
            q,
            (snapshot) => {
                const paymentsData: SellerPayment[] = snapshot.docs
                    .map(doc => {
                        const data = doc.data();
                        return {
                            id: doc.id,
                            ...data,
                            date: data.date?.toDate?.()?.toISOString() || data.date || new Date().toISOString(),
                            createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt || new Date().toISOString()
                        } as SellerPayment;
                    })
                    .filter(payment => !payment.archived); // Filter out archived payments
                setSellerPayments(paymentsData);
            },
            (error) => {
                console.error('Error fetching seller payments:', error);
            }
        );
        return () => unsubscribe();
    }, [authEpoch]);

    // Fetch and listen to lostPackageReports collection
    useEffect(() => {
        if (authEpoch === null) return; // wait for auth to settle
        const q = query(collection(db, 'lostPackageReports'), orderBy('createdAt', 'desc'));
        const unsubscribe = onSnapshot(
            q,
            async (snapshot) => {
                const reportsData: LostPackageReport[] = snapshot.docs
                    .map(doc => {
                        const data = doc.data();
                        return {
                            id: doc.id,
                            ...data,
                            createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt || new Date().toISOString(),
                            reviewedAt: data.reviewedAt?.toDate?.()?.toISOString() || data.reviewedAt
                        } as LostPackageReport;
                    })
                    .filter(report => !report.archived); // Filter out archived reports
                setLostPackageReports(reportsData);
                
                // Check all active posts when lost reports change (to catch stock depletion)
                // Wait a bit for requests to be updated
                setTimeout(async () => {
                    const activePosts = posts.filter(p => !p.archived);
                    const affectedRequestIds = new Set(reportsData.map(r => r.requestId));
                    const affectedPostIds = new Set(
                        requests
                            .filter(r => affectedRequestIds.has(r.id))
                            .map(r => r.postId)
                    );
                    for (const post of activePosts) {
                        if (affectedPostIds.has(post.id)) {
                            await checkAndArchivePostIfStockDepleted(post.id);
                        }
                    }
                }, 500);
            },
            (error) => {
                console.error('Error fetching lost package reports:', error);
            }
        );
        return () => unsubscribe();
    }, [posts, requests, authEpoch]);

    // Fetch and listen to withdrawalRequests collection
    useEffect(() => {
        if (authEpoch === null) return; // wait for auth to settle
        const q = query(collection(db, 'withdrawalRequests'), orderBy('createdAt', 'desc'));
        const unsubscribe = onSnapshot(
            q,
            (snapshot) => {
                const requestsData: WithdrawalRequest[] = snapshot.docs
                    .map(doc => {
                        const data = doc.data();
                        return {
                            id: doc.id,
                            ...data,
                            createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt || new Date().toISOString(),
                            reviewedAt: data.reviewedAt?.toDate?.()?.toISOString() || data.reviewedAt
                        } as WithdrawalRequest;
                    })
                    .filter(request => !request.archived); // Filter out archived withdrawal requests
                setWithdrawalRequests(requestsData);
            },
            (error) => {
                console.error('Error fetching withdrawal requests:', error);
            }
        );
        return () => unsubscribe();
    }, [authEpoch]);

    // Fetch and listen to pendingTransfers collection
    useEffect(() => {
        if (authEpoch === null) return; // wait for auth to settle
        const q = query(collection(db, 'pendingTransfers'), orderBy('createdAt', 'desc'));
        const unsubscribe = onSnapshot(
            q,
            (snapshot) => {
                const transfersData: PendingTransfer[] = snapshot.docs
                    .map(doc => {
                        const data = doc.data();
                        return {
                            id: doc.id,
                            ...data,
                            createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt || new Date().toISOString(),
                            completedAt: data.completedAt?.toDate?.()?.toISOString() || data.completedAt
                        } as PendingTransfer;
                    })
                    .filter(transfer => !transfer.archived); // Filter out archived transfers
                setPendingTransfers(transfersData);
            },
            (error) => {
                console.error('Error fetching pending transfers:', error);
            }
        );
        return () => unsubscribe();
    }, [authEpoch]);

    // Fetch and listen to userApprovalRequests collection
    useEffect(() => {
        if (authEpoch === null) return; // wait for auth to settle
        const q = query(collection(db, 'userApprovalRequests'), orderBy('createdAt', 'desc'));
        const unsubscribe = onSnapshot(
            q,
            (snapshot) => {
                const requestsData: UserApprovalRequest[] = snapshot.docs
                    .map(doc => ({
                        id: doc.id,
                        ...doc.data(),
                        createdAt: doc.data().createdAt?.toDate?.()?.toISOString() || doc.data().createdAt || new Date().toISOString(),
                        reviewedAt: doc.data().reviewedAt?.toDate?.()?.toISOString() || doc.data().reviewedAt
                    } as UserApprovalRequest))
                    .filter(req => !req.archived);
                setUserApprovalRequests(requestsData);
            },
            (error) => {
                console.error('Error fetching user approval requests:', error);
            }
        );
        return () => unsubscribe();
    }, [authEpoch]);

    // Fetch and listen to shortageDeliveries collection
    useEffect(() => {
        if (authEpoch === null) return; // wait for auth to settle
        const q = query(collection(db, 'shortageDeliveries'), orderBy('createdAt', 'desc'));
        const unsubscribe = onSnapshot(
            q,
            (snapshot) => {
                const shortagesData: ShortageDelivery[] = snapshot.docs
                    .map(doc => {
                        const data = doc.data();
                        return {
                            id: doc.id,
                            ...data,
                            createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt || new Date().toISOString(),
                            reviewedAt: data.reviewedAt?.toDate?.()?.toISOString() || data.reviewedAt,
                            missingKgDeliveredAt: data.missingKgDeliveredAt?.toDate?.()?.toISOString() || data.missingKgDeliveredAt,
                            finalActionApprovedAt: data.finalActionApprovedAt?.toDate?.()?.toISOString() || data.finalActionApprovedAt
                        } as ShortageDelivery;
                    })
                    .filter(shortage => !shortage.archived);
                setShortageDeliveries(shortagesData);
            },
            (error) => {
                console.error('Error fetching shortage deliveries:', error);
            }
        );
        return () => unsubscribe();
    }, [authEpoch]);

    // Fetch and listen to offices collection
    useEffect(() => {
        if (authEpoch === null) return; // wait for auth to settle
        const q = query(collection(db, 'offices'), orderBy('createdAt', 'desc'));
        const unsubscribe = onSnapshot(
            q,
            (snapshot) => {
                const officesData: Office[] = snapshot.docs
                    .map(doc => {
                        const data = doc.data();
                        return {
                            id: doc.id,
                            ...data,
                            createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt || new Date().toISOString()
                        } as Office;
                    })
                    .filter(office => !office.archived); // Filter out archived offices
                setOffices(officesData);
            },
            (error) => {
                console.error('Error fetching offices:', error);
            }
        );
        return () => unsubscribe();
    }, [authEpoch]);

    // Fetch and listen to notifications collection (will be filtered by user in components)
    useEffect(() => {
        if (authEpoch === null) return; // wait for auth to settle
        const q = query(collection(db, 'notifications'), orderBy('createdAt', 'desc'));
        const unsubscribe = onSnapshot(
            q,
            (snapshot) => {
                const notificationsData: Notification[] = snapshot.docs
                    .map(doc => {
                        const data = doc.data();
                        return {
                            id: doc.id,
                            ...data,
                            createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt || new Date().toISOString(),
                            readAt: data.readAt?.toDate?.()?.toISOString() || data.readAt
                        } as Notification;
                    })
                    .filter(notification => !notification.archived); // Filter out archived notifications
                setNotifications(notificationsData);
            },
            (error) => {
                console.error('Error fetching notifications:', error);
            }
        );
        return () => unsubscribe();
    }, [authEpoch]);

    const addRequest = async (request: Omit<ReservationRequest, 'id' | 'createdAt' | 'status'>) => {
        try {
            const now = Timestamp.now();
            const requestRef = await addDoc(collection(db, 'requests'), {
                ...request,
                status: RequestStatus.Pending,
                createdAt: now,
                statusHistory: [{
                    status: RequestStatus.Pending,
                    timestamp: now
                }]
            });
            
            // Send notification to office about new package request
            const post = posts.find(p => p.id === request.postId);
            if (post && post.officeId) {
                const user = users.find(u => u.id === request.userId);
                const currentLang = (localStorage.getItem('language') as 'ar' | 'tr') || 'tr';
                const title = currentLang === 'ar' ? 'طلب طرد جديد' : 'Yeni Paket Talebi';
                const message = currentLang === 'ar'
                    ? `طلب جديد من ${user?.name || 'مستخدم'} لـ ${request.requestedKg} ${post.unit || 'kg'} من ${post.fromCity} إلى ${post.toCity}`
                    : `${user?.name || 'Kullanıcı'} tarafından ${post.fromCity} → ${post.toCity} için ${request.requestedKg} ${post.unit || 'kg'} yeni paket talebi`;
                await sendNotificationToOffice(
                    users,
                    post.officeId,
                    NotificationType.PackageRequest,
                    title,
                    message,
                    requestRef.id,
                    { userId: request.userId, requestedKg: request.requestedKg, postId: post.id }
                );
            }
        } catch (error) {
            console.error('Error adding request:', error);
            throw error;
        }
    };

    const updateRequestStatus = async (requestId: string, status: RequestStatus, qrCodeData?: string, workerId?: string) => {
        try {
            const requestRef = doc(db, 'requests', requestId);
            // Get the current request from Firestore to ensure we have the latest data
            const requestDoc = await getDoc(requestRef);
            let request: ReservationRequest | undefined;
            
            if (requestDoc.exists()) {
                const docData = requestDoc.data();
                const statusHistory = docData.statusHistory?.map((entry: any) => ({
                    status: entry.status,
                    timestamp: entry.timestamp?.toDate?.()?.toISOString() || entry.timestamp || new Date().toISOString()
                })) || [];
                
                request = {
                    id: requestDoc.id,
                    ...docData,
                    createdAt: docData.createdAt?.toDate?.()?.toISOString() || docData.createdAt || new Date().toISOString(),
                    statusHistory
                } as ReservationRequest;
            } else {
                throw new Error(`Request with id ${requestId} not found`);
            }
            
            const now = Timestamp.now();
            
            // Get existing status history or initialize it with Pending status
            let existingHistory = request?.statusHistory || [];
            
            // If no history exists, initialize with Pending status at creation time
            if (existingHistory.length === 0 && request) {
                const createdAtIso = typeof request.createdAt === 'string'
                    ? request.createdAt
                    : new Date().toISOString();
                existingHistory = [{
                    status: RequestStatus.Pending,
                    timestamp: createdAtIso
                }];
            }
            
            // Get the last status from history or current status
            const lastStatus = existingHistory.length > 0 
                ? existingHistory[existingHistory.length - 1].status 
                : (request?.status || RequestStatus.Pending);
            
            const statusHistory: any[] = [...existingHistory];
            
            // Only add to history if status is actually changing
            if (lastStatus !== status) {
                statusHistory.push({
                    status: status,
                    timestamp: now
                });
            }
            
            const updateData: any = { 
                status,
                statusHistory: statusHistory.map(entry => {
                    // Convert timestamp to Firestore Timestamp if it's a string
                    let timestamp = entry.timestamp;
                    if (typeof timestamp === 'string') {
                        timestamp = Timestamp.fromDate(new Date(timestamp));
                    } else if (!(timestamp instanceof Timestamp)) {
                        timestamp = Timestamp.fromDate(new Date(timestamp));
                    }
                    return {
                        status: entry.status,
                        timestamp: timestamp
                    };
                })
            };
            
            if (qrCodeData) {
                updateData.qrCodeData = qrCodeData;
                updateData.packageId = `pkg-${requestId}`;
                if (workerId) {
                    updateData.qrGeneratedBy = workerId;
                    // When QR is generated and status changes to InTransit, also set receivedBy
                    if (status === RequestStatus.InTransit && lastStatus !== RequestStatus.InTransit) {
                        updateData.receivedBy = workerId;
                    }
                }
            }
            
            // Track approvedBy when request is approved (by admin, office, or worker)
            if (status === RequestStatus.Approved && lastStatus !== RequestStatus.Approved) {
                if (workerId) {
                    updateData.approvedBy = workerId;
                }
            }
            
            if (workerId) {
                // Track receivedBy when package is accepted (status changes to InTransit)
                // Only set if not already set by QR generation above
                if (status === RequestStatus.InTransit && lastStatus !== RequestStatus.InTransit && !updateData.receivedBy) {
                    updateData.receivedBy = workerId;
                }
                // Track deliveredBy when package is marked as delivered
                if (status === RequestStatus.Delivered) {
                    updateData.deliveredBy = workerId;
                }
                // Track scannedBy for any status change when done by a worker
                updateData.scannedBy = workerId;
            }
            
            await updateDoc(requestRef, updateData);
            
            // Send notification when package is approved
            if (status === RequestStatus.Approved && lastStatus !== RequestStatus.Approved && request) {
                const post = posts.find(p => p.id === request.postId);
                const user = users.find(u => u.id === request.userId);
                if (user && post) {
                    const currentLang = (localStorage.getItem('language') as 'ar' | 'tr') || 'tr';
                    const title = currentLang === 'ar' ? 'تم الموافقة على الطرد' : 'Paket Onaylandı';
                    const message = currentLang === 'ar' 
                        ? `تم الموافقة على طلبك للطرد من ${post.fromCity} إلى ${post.toCity}`
                        : `${post.fromCity} → ${post.toCity} paket talebiniz onaylandı`;
                    await createNotification(
                        request.userId,
                        NotificationType.PackageApproval,
                        title,
                        message,
                        requestId
                    );
                }
            }
            
            // NOTE: Office balance is now only updated when products are added with payment type "before"
            // Removed automatic office credit transaction when requests are approved
            // This prevents incorrect balance increases when users take packages
            
            // Check if post should be archived after status change
            // Only check when status changes to Approved, InTransit, or Delivered
            if (request && (status === RequestStatus.Approved || status === RequestStatus.InTransit || status === RequestStatus.Delivered)) {
                await checkAndArchivePostIfStockDepleted(request.postId);
            }
        } catch (error) {
            console.error('Error updating request status:', error);
            throw error;
        }
    };
    
    const addPost = async (post: Omit<ShipmentPost, 'id' | 'createdAt'>) => {
        try {
            await addDoc(collection(db, 'posts'), {
                ...post,
                createdAt: Timestamp.now()
            });
        } catch (error) {
            console.error('Error adding post:', error);
            throw error;
        }
    };

    const updatePost = async (updatedPost: ShipmentPost) => {
        try {
            const postRef = doc(db, 'posts', updatedPost.id);
            const { id, createdAt, archivedAt, ...postData } = updatedPost;
            
            // Filter out undefined values - Firestore doesn't allow undefined
            const cleanData: any = {};
            Object.keys(postData).forEach(key => {
                const value = (postData as any)[key];
                if (value !== undefined) {
                    cleanData[key] = value;
                }
            });
            
            // Handle createdAt separately - convert string to Timestamp if needed
            cleanData.createdAt = createdAt ? Timestamp.fromDate(new Date(createdAt)) : Timestamp.now();
            
            // Handle archivedAt separately - only include if it exists, convert string to Timestamp if needed
            if (archivedAt !== undefined) {
                if (typeof archivedAt === 'string') {
                    cleanData.archivedAt = Timestamp.fromDate(new Date(archivedAt));
                } else {
                    cleanData.archivedAt = archivedAt;
                }
            }
            
            await updateDoc(postRef, cleanData);
        } catch (error) {
            console.error('Error updating post:', error);
            throw error;
        }
    };
    
    const markPackageDelivered = async (requestId: string, workerId?: string, receivedKg?: number, shortageAction?: 'deduct' | 'cash') => {
        try {
            const request = requests.find(r => r.id === requestId);
            if (!request) {
                throw new Error('Request not found');
            }

            const post = posts.find(p => p.id === request.postId);
            if (!post) {
                throw new Error('Post not found');
            }

            // Validate that only the office that created the QR code can accept the package
            // This validation MUST run if there's a QR code
            if (request.qrCodeData) {
                // Extract officeId from the stored QR code data
                let qrOfficeId: string | null = null;
                try {
                    // Try to parse the QR code data (it's stored as JSON string)
                    const qrData = JSON.parse(request.qrCodeData);
                    qrOfficeId = qrData.officeId || null;
                } catch (e) {
                    // If parsing fails, we can't validate - reject for security
                    throw new Error('Invalid QR code data. Cannot verify office authorization.');
                }

                if (qrOfficeId) {
                    // Get the current user (worker) who is trying to accept the package
                    if (!workerId) {
                        throw new Error('User ID is required to accept packages.');
                    }

                    const currentUser = users.find(u => u.id === workerId);
                    if (!currentUser) {
                        throw new Error('User not found.');
                    }

                    // Determine current user's office ID
                    let currentOfficeId = '';
                    if (currentUser.role === UserRole.Office) {
                        currentOfficeId = currentUser.officeId || '';
                    } else if (currentUser.role === UserRole.Worker) {
                        currentOfficeId = currentUser.officeId || '';
                    } else if (currentUser.role === UserRole.AdminA || currentUser.role === UserRole.AdminB) {
                        // For backward compatibility, use adminId as officeId for old admins
                        currentOfficeId = currentUser.id;
                    } else if (currentUser.role === UserRole.SuperAdmin) {
                        // SuperAdmin can accept packages from any office
                        currentOfficeId = qrOfficeId; // Allow SuperAdmin to accept
                    }

                    // STRICT VALIDATION: Check if offices match
                    // Only allow if currentOfficeId matches qrOfficeId (or SuperAdmin)
                    if (currentUser.role !== UserRole.SuperAdmin && currentOfficeId !== qrOfficeId) {
                        throw new Error('Only the office that created this QR code can accept the package.');
                    }
                } else {
                    // QR code exists but doesn't have officeId - this is an old QR code
                    // For security, we should reject it or require re-generation
                    // But for backward compatibility, we'll validate using post.officeId as fallback
                    if (post.officeId && workerId) {
                        const currentUser = users.find(u => u.id === workerId);
                        if (currentUser) {
                            let currentOfficeId = '';
                            if (currentUser.role === UserRole.Office) {
                                currentOfficeId = currentUser.officeId || '';
                            } else if (currentUser.role === UserRole.Worker) {
                                currentOfficeId = currentUser.officeId || '';
                            } else if (currentUser.role === UserRole.AdminA || currentUser.role === UserRole.AdminB) {
                                currentOfficeId = currentUser.id;
                            } else if (currentUser.role === UserRole.SuperAdmin) {
                                currentOfficeId = post.officeId; // Allow SuperAdmin to accept
                            }

                            // Check if offices match
                            if (currentUser.role !== UserRole.SuperAdmin && currentOfficeId && currentOfficeId !== post.officeId) {
                                throw new Error('Only the office that created this QR code can accept the package.');
                            }
                        }
                    }
                }
            }

            // Use received weight if provided, otherwise use requested weight
            const actualKg = receivedKg !== undefined && receivedKg !== null ? receivedKg : request.requestedKg;

            // Check for shortage (receivedKg < requestedKg)
            const hasShortage = receivedKg !== undefined && receivedKg !== null && receivedKg < request.requestedKg;
            const missingKg = hasShortage ? (request.requestedKg - receivedKg) : 0;

            // If there's a shortage, shortageAction must be provided
            if (hasShortage && !shortageAction) {
                throw new Error('Shortage action required when package has missing items');
            }

            // 1. Update request status and save receivedKg
            await updateRequestStatus(requestId, RequestStatus.Delivered, undefined, workerId);
            
            // Save receivedKg to request if it was provided and differs from requestedKg
            if (receivedKg !== undefined && receivedKg !== null) {
                const requestRef = doc(db, 'requests', requestId);
                await updateDoc(requestRef, {
                    receivedKg: receivedKg
                });
            }

            // 2. Add credit to user balance based on actual received weight
            // Use pricePerKg (what user paid), not maliyet
            const costPerKg = post.pricePerKg || 0;
            const creditAmount = actualKg * costPerKg;
            const userRef = doc(db, 'users', request.userId);
            await updateDoc(userRef, {
                balance: increment(creditAmount)
            });

            // 3. Add transaction record
            await addDoc(collection(db, 'transactions'), {
                userId: request.userId,
                amount: creditAmount,
                type: TransactionType.Credit,
                date: Timestamp.now(),
                description: `Credit for package ${request.packageId || `pkg-${requestId}`} (${actualKg} ${post.unit || 'kg'} received${hasShortage ? `, expected ${request.requestedKg} ${post.unit || 'kg'}` : ''})`
            });
            
            // Send package receiving notification and balance notification
            const user = users.find(u => u.id === request.userId);
            if (user) {
                const currentLang = (localStorage.getItem('language') as 'ar' | 'tr') || 'tr';
                const title = currentLang === 'ar' ? 'تم استلام الطرد' : 'Paket Teslim Alındı';
                const message = currentLang === 'ar'
                    ? `تم استلام طردك من ${post.fromCity} إلى ${post.toCity}. تم إضافة ${creditAmount.toFixed(2)} إلى رصيدك`
                    : `${post.fromCity} → ${post.toCity} paketiniz teslim alındı. ${creditAmount.toFixed(2)} bakiyenize eklendi`;
                await createNotification(
                    request.userId,
                    NotificationType.PackageReceiving,
                    title,
                    message,
                    requestId,
                    { amount: creditAmount, postId: post.id }
                );
                
                // Also send balance notification
                const balanceTitle = currentLang === 'ar' ? 'تحديث الرصيد' : 'Bakiye Güncellendi';
                const balanceMessage = currentLang === 'ar'
                    ? `تم إضافة ${creditAmount.toFixed(2)} إلى رصيدك. الرصيد الجديد: ${(user.balance + creditAmount).toFixed(2)}`
                    : `${creditAmount.toFixed(2)} bakiyenize eklendi. Yeni bakiye: ${(user.balance + creditAmount).toFixed(2)}`;
                await createNotification(
                    request.userId,
                    NotificationType.BalanceNotification,
                    balanceTitle,
                    balanceMessage,
                    requestId,
                    { amount: creditAmount, newBalance: user.balance + creditAmount }
                );
            }
            
            // 4. Handle shortage delivery
            if (hasShortage && shortageAction) {
                // Get maliyet (cost) for deduction calculation
                const postAny = post as any;
                let product = null;
                if (postAny?.productId) {
                    product = products.find(p => p.id === postAny.productId);
                } else if (postAny?.sellerId) {
                    product = products.find(p => 
                        p.sellerId === postAny.sellerId &&
                        p.fromCity === post.fromCity &&
                        p.toCity === post.toCity &&
                        p.fromCountry === post.fromCountry &&
                        p.toCountry === post.toCountry
                    );
                }
                
                // Use maliyet from product or post, fallback to pricePerKg
                const maliyet = (product as any)?.maliyet || post.maliyet || post.pricePerKg || 0;
                const missingAmount = missingKg * maliyet;

                if (shortageAction === 'deduct') {
                    // Deduct from user balance using maliyet
                    await updateDoc(userRef, {
                        balance: increment(-missingAmount)
                    });
                    
                    // Add transaction record for deduction
                    // Get current language from localStorage or default to 'tr'
                    const currentLang = (localStorage.getItem('language') as 'ar' | 'tr') || 'tr';
                    const unit = post.unit || 'kg';
                    const unitLabel = unit === 'piece' ? (translations[currentLang].piece || 'piece') : unit;
                    const perUnitLabel = unit === 'piece' ? (translations[currentLang].perPiece || 'per piece') : (translations[currentLang].perKg || 'per kg');
                    const packageId = request.packageId || requestId;
                    const deductionText = (translations[currentLang].deductionForMissing || 'Deduction for missing {unit}: {missingAmount} {unit} from package {packageId} (maliyet: {maliyet} {perUnit})')
                        .replace('{unit}', unitLabel)
                        .replace('{missingAmount}', missingKg.toString())
                        .replace('{packageId}', packageId)
                        .replace('{maliyet}', maliyet.toString())
                        .replace('{perUnit}', perUnitLabel);
                    
                    await addDoc(collection(db, 'transactions'), {
                        userId: request.userId,
                        amount: missingAmount,
                        type: TransactionType.Withdrawal,
                        date: Timestamp.now(),
                        description: deductionText
                    });
                }
                // If 'cash', no deduction needed - just record it
                
                // Determine receiving office: worker's office or post's office as fallback
                let receivingOfficeId = post.officeId;
                if (workerId) {
                    const worker = users.find(u => u.id === workerId);
                    if (worker && worker.officeId) {
                        receivingOfficeId = worker.officeId;
                    }
                }
                
                // Create shortage delivery record with the action taken
                await addDoc(collection(db, 'shortageDeliveries'), {
                    requestId: requestId,
                    userId: request.userId,
                    postId: request.postId,
                    sendingOfficeId: post.officeId,
                    receivingOfficeId: receivingOfficeId,
                    requestedKg: request.requestedKg,
                    receivedKg: receivedKg,
                    missingKg: missingKg,
                    status: shortageAction === 'deduct' ? ShortageDeliveryStatus.BalanceDeducted : ShortageDeliveryStatus.PaidUpfront,
                    action: shortageAction === 'deduct' ? 'deduct' : 'hold',
                    reviewedAt: Timestamp.now(),
                    reviewedBy: workerId || undefined,
                    createdAt: Timestamp.now()
                });
            }
            
            // 5. Check if post should be archived after delivery
            await checkAndArchivePostIfStockDepleted(post.id);
        } catch (error) {
            console.error('Error marking package as delivered:', error);
            throw error;
        }
    };

    const addSeller = async (seller: Omit<Seller, 'id' | 'createdAt'>) => {
        try {
            await addDoc(collection(db, 'sellers'), {
                ...seller,
                createdAt: Timestamp.now()
            });
        } catch (error) {
            console.error('Error adding seller:', error);
            throw error;
        }
    };

    const updateSeller = async (updatedSeller: Seller) => {
        try {
            const sellerRef = doc(db, 'sellers', updatedSeller.id);
            const { id, createdAt, ...sellerData } = updatedSeller;
            await updateDoc(sellerRef, {
                ...sellerData,
                createdAt: createdAt ? Timestamp.fromDate(new Date(createdAt)) : Timestamp.now()
            });
        } catch (error) {
            console.error('Error updating seller:', error);
            throw error;
        }
    };

    const addProduct = async (product: Omit<Product, 'id' | 'createdAt'>) => {
        try {
            await addDoc(collection(db, 'products'), {
                ...product,
                createdAt: Timestamp.now()
            });
        } catch (error) {
            console.error('Error adding product:', error);
            throw error;
        }
    };

    const updateProduct = async (updatedProduct: Product) => {
        try {
            const productRef = doc(db, 'products', updatedProduct.id);
            const { id, createdAt, ...productData } = updatedProduct;
            await updateDoc(productRef, {
                ...productData,
                createdAt: createdAt ? Timestamp.fromDate(new Date(createdAt)) : Timestamp.now()
            });
        } catch (error) {
            console.error('Error updating product:', error);
            throw error;
        }
    };

    const deleteSeller = async (sellerId: string) => {
        try {
            const sellerRef = doc(db, 'sellers', sellerId);
            await updateDoc(sellerRef, {
                archived: true,
                archivedAt: Timestamp.now()
            });
        } catch (error) {
            console.error('Error archiving seller:', error);
            throw error;
        }
    };

    const deleteProduct = async (productId: string) => {
        try {
            const productRef = doc(db, 'products', productId);
            await updateDoc(productRef, {
                archived: true,
                archivedAt: Timestamp.now()
            });
        } catch (error) {
            console.error('Error archiving product:', error);
            throw error;
        }
    };

    const deletePost = async (postId: string) => {
        try {
            const postRef = doc(db, 'posts', postId);
            await updateDoc(postRef, {
                archived: true,
                archivedAt: Timestamp.now()
            });
        } catch (error) {
            console.error('Error archiving post:', error);
            throw error;
        }
    };

    const checkAndArchiveAllPosts = async () => {
        try {
            console.log('Starting check for all posts with depleted stock...');
            const activePosts = posts.filter(p => !p.archived);
            console.log(`Found ${activePosts.length} active posts to check`);
            
            for (const post of activePosts) {
                await checkAndArchivePostIfStockDepleted(post.id);
            }
            
            console.log('Finished checking all posts');
        } catch (error) {
            console.error('Error checking all posts:', error);
            throw error;
        }
    };

    const transferPackage = async (requestId: string, newUserId: string, giverId?: string, viaTransferId?: string) => {
        try {
            const requestRef = doc(db, 'requests', requestId);
            const request = requests.find(r => r.id === requestId);

            if (!request) {
                throw new Error('Request not found');
            }

            // Check if there's an approved lost package report for this request
            const approvedReport = lostPackageReports.find(
                r => r.requestId === requestId && r.status === LostPackageReportStatus.Approved
            );

            if (approvedReport) {
                throw new Error('Cannot transfer package: Lost package report has been approved and balance has been deducted.');
            }

            // Store the original owner before transferring
            const originalUserId = request.userId;
            const now = Timestamp.now();

            // Update userId to receiver and add transfer information.
            // `transferViaId` references the confirmed pendingTransfers doc so
            // the security rules can verify a receiver-initiated completion.
            const transferUpdate: any = {
                userId: newUserId,
                transferredFromUserId: originalUserId,
                transferredAt: now,
                transferredBy: giverId || originalUserId
            };
            if (viaTransferId) {
                transferUpdate.transferViaId = viaTransferId;
            }
            await updateDoc(requestRef, transferUpdate);
            
            // Set status to Transferred and update status history
            // The receiver will see it as active because the filter includes Transferred status
            await updateRequestStatus(requestId, RequestStatus.Transferred);
        } catch (error) {
            console.error('Error transferring package:', error);
            throw error;
        }
    };

    const createPendingTransfer = async (requestId: string, giverId: string, receiverId: string): Promise<string> => {
        try {
            const now = Timestamp.now();
            const transferRef = await addDoc(collection(db, 'pendingTransfers'), {
                requestId,
                giverId,
                receiverId,
                giverConfirmed: false,
                receiverConfirmed: false,
                status: 'pending',
                createdAt: now
            });
            return transferRef.id;
        } catch (error) {
            console.error('Error creating pending transfer:', error);
            throw error;
        }
    };

    const confirmPendingTransfer = async (transferId: string, userId: string) => {
        try {
            const transferRef = doc(db, 'pendingTransfers', transferId);
            const transferDoc = await getDoc(transferRef);
            
            if (!transferDoc.exists()) {
                throw new Error('Pending transfer not found');
            }

            const transferData = transferDoc.data() as PendingTransfer;
            
            // Check if user is giver or receiver
            if (transferData.giverId === userId) {
                await updateDoc(transferRef, {
                    giverConfirmed: true
                });
            } else if (transferData.receiverId === userId) {
                await updateDoc(transferRef, {
                    receiverConfirmed: true
                });
            } else {
                throw new Error('User is not authorized to confirm this transfer');
            }

            // Check if both have confirmed, then execute transfer
            const updatedDoc = await getDoc(transferRef);
            const updatedData = updatedDoc.data() as PendingTransfer;
            
            if (updatedData.giverConfirmed && updatedData.receiverConfirmed) {
                // Execute the transfer (referencing this confirmed pending
                // transfer so security rules can validate the completion)
                await transferPackage(transferData.requestId, transferData.receiverId, transferData.giverId, transferId);
                
                // Mark transfer as completed
                await updateDoc(transferRef, {
                    status: 'completed',
                    completedAt: Timestamp.now()
                });
            }
        } catch (error) {
            console.error('Error confirming pending transfer:', error);
            throw error;
        }
    };

    const cancelPendingTransfer = async (transferId: string) => {
        try {
            const transferRef = doc(db, 'pendingTransfers', transferId);
            await updateDoc(transferRef, {
                status: 'cancelled'
            });
        } catch (error) {
            console.error('Error cancelling pending transfer:', error);
            throw error;
        }
    };

    const reportLostPackage = async (requestId: string, description: string, file?: File) => {
        try {
            const request = requests.find(r => r.id === requestId);
            if (!request) {
                throw new Error('Request not found');
            }

            // Check if there's already a pending report for this request
            const existingReport = lostPackageReports.find(
                r => r.requestId === requestId && r.status === LostPackageReportStatus.Pending
            );
            if (existingReport) {
                throw new Error('A pending report already exists for this package');
            }

            let fileUrl: string | undefined;
            let fileName: string | undefined;

            // Upload file if provided
            if (file) {
                try {
                    // Create a unique file name
                    const timestamp = Date.now();
                    const fileExtension = file.name.split('.').pop();
                    const fileNameWithTimestamp = `lost-package-${requestId}-${timestamp}.${fileExtension}`;
                    const storageRef = ref(storage, `lostPackageReports/${fileNameWithTimestamp}`);
                    
                    // Upload file
                    await uploadBytes(storageRef, file);
                    
                    // Get download URL
                    fileUrl = await getDownloadURL(storageRef);
                    fileName = file.name;
                } catch (uploadError) {
                    console.error('Error uploading file:', uploadError);
                    throw new Error('Failed to upload file. Please try again.');
                }
            }

            await addDoc(collection(db, 'lostPackageReports'), {
                requestId,
                userId: request.userId,
                description,
                status: LostPackageReportStatus.Pending,
                createdAt: Timestamp.now(),
                ...(fileUrl && { fileUrl }),
                ...(fileName && { fileName })
            });
        } catch (error) {
            console.error('Error reporting lost package:', error);
            throw error;
        }
    };

    // ----- Account creation helpers -----

    /**
     * Whether the main app session is a real (non-anonymous) Firebase user.
     * Staff callers write documents with their own identity; self-registration
     * writes them under the freshly created account's identity instead.
     */
    const hasRealFirebaseSession = (): boolean =>
        !!primaryAuth?.currentUser && !primaryAuth.currentUser.isAnonymous;

    /**
     * Create a user document backed by a real Firebase Auth account.
     * The password is used ONLY for the Auth account and never stored in
     * Firestore. Falls back to the legacy plain-document behaviour if the
     * Email/Password provider is not enabled yet, so nothing breaks during
     * the transition.
     */
    const createUserWithAuthAccount = async (userData: Omit<User, 'id'>, password: string): Promise<string> => {
        const email = userData.email && isEmail(userData.email)
            ? userData.email.trim().toLowerCase()
            : (userData.username ? syntheticEmailForUsername(userData.username) : undefined);

        const { password: _password, ...docData } = userData as any;

        if (email && primaryAuth) {
            const staffCaller = hasRealFirebaseSession();
            let created;
            try {
                created = await createAuthAccount(email, password);
            } catch (authError: any) {
                const code = authError?.code || '';
                if (code === 'auth/email-already-in-use') {
                    throw new Error('An account with this email already exists.');
                }
                if (code === 'auth/operation-not-allowed' || code === 'auth/configuration-not-found') {
                    // Provider not enabled yet — legacy fallback (transition only).
                    console.warn('[DATA] Email/Password provider not enabled; storing legacy credentials');
                    const legacyRef = await addDoc(collection(db, 'users'), { ...docData, password });
                    return legacyRef.id;
                }
                throw authError;
            }
            try {
                const database = staffCaller ? db : getSecondaryDb();
                await setDoc(firestoreDoc(database, 'users', created.uid), {
                    ...docData,
                    authEmail: created.email
                });
                await writeAuthMap(created.uid, created.uid, !staffCaller);
                if (userData.username) {
                    await writeLoginAlias(userData.username, created.email, !staffCaller);
                }
            } finally {
                await finishAccountCreation();
            }
            return created.uid;
        }

        // No usable email — plain profile document without login capability.
        const ref2 = await addDoc(collection(db, 'users'), docData);
        return ref2.id;
    };

    /**
     * Queue a password change to be applied to the Firebase Auth account by
     * the admin tooling (scripts/admin/process-password-resets.mjs). Clients
     * cannot change another user's Auth password directly. The queue is
     * write-only for clients (see firestore.rules).
     */
    const queuePasswordReset = async (userId: string, targetUser: User | undefined, newPassword: string) => {
        await addDoc(collection(db, 'authPasswordResets'), {
            userId,
            email: (targetUser as any)?.authEmail || targetUser?.email || (targetUser?.username ? syntheticEmailForUsername(targetUser.username) : null),
            newPassword,
            status: 'pending',
            requestedAt: Timestamp.now()
        });
    };

    // Office Management Functions
    const addOffice = async (office: Omit<Office, 'id' | 'createdAt'>): Promise<string> => {
        try {
            const { password: officePassword, ...officeDoc } = office as any;
            const officeRef = await addDoc(collection(db, 'offices'), {
                ...officeDoc,
                createdAt: Timestamp.now()
            });

            // Create the office user account with a real Firebase Auth login.
            await createUserWithAuthAccount({
                name: office.name,
                email: office.email,
                role: UserRole.Office,
                balance: 0,
                officeId: officeRef.id,
                officeName: office.name,
                username: office.username
            } as Omit<User, 'id'>, officePassword || '');

            return officeRef.id;
        } catch (error) {
            console.error('Error adding office:', error);
            throw error;
        }
    };

    const updateOffice = async (updatedOffice: Office) => {
        try {
            const officeRef = doc(db, 'offices', updatedOffice.id);
            // Never persist passwords in Firestore documents.
            const { id, createdAt, password: newPassword, ...officeData } = updatedOffice as any;
            await updateDoc(officeRef, officeData);

            // Also update the corresponding user account
            const officeUser = users.find(u => u.officeId === updatedOffice.id && u.role === UserRole.Office);
            if (officeUser) {
                const userRef = doc(db, 'users', officeUser.id);
                const updateData: any = {
                    name: updatedOffice.name,
                    email: updatedOffice.email,
                    username: updatedOffice.username,
                    officeName: updatedOffice.name
                };
                await updateDoc(userRef, updateData);

                // Password changes are applied to the Firebase Auth account by
                // the admin tooling; queue the request instead of storing it.
                if (newPassword) {
                    await queuePasswordReset(officeUser.id, officeUser, newPassword);
                }
            }
        } catch (error) {
            console.error('Error updating office:', error);
            throw error;
        }
    };

    const deleteOffice = async (officeId: string) => {
        try {
            // Archive the office document
            const officeRef = doc(db, 'offices', officeId);
            await updateDoc(officeRef, {
                archived: true,
                archivedAt: Timestamp.now()
            });
            
            // Find and archive the office user account
            const officeUser = users.find(u => u.officeId === officeId && u.role === UserRole.Office);
            if (officeUser) {
                await deleteUser(officeUser.id);
            }
            
            // Also archive all workers belonging to this office
            const officeWorkers = users.filter(u => u.officeId === officeId && u.role === UserRole.Worker);
            for (const worker of officeWorkers) {
                await deleteUser(worker.id);
            }
        } catch (error) {
            console.error('Error archiving office:', error);
            throw error;
        }
    };

    const addUser = async (userData: Omit<User, 'id'>) => {
        try {
            const password = (userData as any).password as string | undefined;
            if (password) {
                // Login-capable account: back it with Firebase Auth, never
                // store the password in Firestore.
                await createUserWithAuthAccount(userData, password);
                return;
            }
            // Profile-only document (e.g. manual package recipients).
            const { password: _pw, ...docData } = userData as any;
            await addDoc(collection(db, 'users'), docData);
        } catch (error) {
            console.error('Error adding user:', error);
            throw error;
        }
    };

    const updateUser = async (updatedUser: User) => {
        try {
            const userRef = doc(db, 'users', updatedUser.id);
            // Never persist passwords in Firestore documents.
            const { id, password: newPassword, ...userData } = updatedUser as any;
            await updateDoc(userRef, userData);

            // A password change (e.g. an office resetting a worker password)
            // is queued for the Firebase Auth account instead of being stored.
            const existing = users.find(u => u.id === updatedUser.id);
            if (newPassword && newPassword !== existing?.password) {
                await queuePasswordReset(updatedUser.id, existing || updatedUser, newPassword);
            }
        } catch (error) {
            console.error('Error updating user:', error);
            throw error;
        }
    };

    const deleteUser = async (userId: string) => {
        try {
            const userRef = doc(db, 'users', userId);
            await updateDoc(userRef, {
                archived: true,
                archivedAt: Timestamp.now()
            });
        } catch (error) {
            console.error('Error archiving user:', error);
            throw error;
        }
    };

    const createManualPackage = async (postId: string, name: string, surname: string, phone: string, requestedKg: number, workerId?: string, bagNumber?: string) => {
        try {
            // Find or create user by phone number
            let userId = '';
            const existingUser = users.find(u => u.phone === phone && u.role === UserRole.Normal && !u.archived);
            
            if (existingUser) {
                userId = existingUser.id;
                // Update user name and surname if provided
                if (name || surname) {
                    const userRef = doc(db, 'users', userId);
                    const updateData: any = {};
                    if (name) updateData.name = name;
                    if (surname) updateData.surname = surname;
                    if (Object.keys(updateData).length > 0) {
                        await updateDoc(userRef, updateData);
                    }
                }
            } else {
                // Create new user
                const newUser: Omit<User, 'id'> = {
                    name: name,
                    surname: surname,
                    email: `manual-${Date.now()}@manual.local`, // Temporary email
                    phone: phone,
                    role: UserRole.Normal,
                    balance: 0,
                    approved: true, // Auto-approve manual packages
                    approvedAt: new Date().toISOString()
                };
                const userRef = await addDoc(collection(db, 'users'), newUser);
                userId = userRef.id;
            }

            // Get the post to find the office ID
            const post = posts.find(p => p.id === postId);
            if (!post) {
                throw new Error('Post not found');
            }

            // Determine the office ID that is creating the package
            let generatingOfficeId = '';
            if (workerId) {
                const worker = users.find(u => u.id === workerId);
                if (worker) {
                    if (worker.role === UserRole.Office) {
                        generatingOfficeId = worker.officeId || '';
                    } else if (worker.role === UserRole.Worker) {
                        generatingOfficeId = worker.officeId || '';
                    } else if (worker.role === UserRole.SuperAdmin) {
                        generatingOfficeId = post.officeId;
                    } else if (worker.role === UserRole.AdminA || worker.role === UserRole.AdminB) {
                        generatingOfficeId = worker.id;
                    }
                }
            }
            
            if (!generatingOfficeId) {
                generatingOfficeId = post.officeId;
            }

            // Create the request with Approved status
            const now = Timestamp.now();
            const requestData: any = {
                userId: userId,
                postId: postId,
                requestedKg: requestedKg,
                status: RequestStatus.Approved,
                approvedBy: workerId,
                createdAt: now,
                statusHistory: [{
                    status: RequestStatus.Pending,
                    timestamp: now
                }, {
                    status: RequestStatus.Approved,
                    timestamp: now
                }]
            };
            
            // Add bag number if provided
            if (bagNumber) {
                requestData.bagNumber = bagNumber;
            }
            
            const requestRef = await addDoc(collection(db, 'requests'), requestData);

            const requestId = requestRef.id;

            // Generate QR code data
            const qrData: any = {
                packageId: `pkg-${requestId}`,
                requestId: requestId,
                postId: postId,
                userId: userId,
                // Embed the recipient name so the public package page can show
                // it without needing read access to the users collection.
                userName: `${name || ''} ${surname || ''}`.trim(),
                requestedKg: requestedKg,
                officeId: generatingOfficeId
            };
            
            // Add bag number to QR code data if provided
            if (bagNumber) {
                qrData.bagNumber = bagNumber;
            }
            const qrString = JSON.stringify(qrData);
            
            // Create URL with base64 encoded data for cleaner QR code
            const base64Data = btoa(qrString);
            // Use arztaleb.com instead of window.location.origin for Android WebView compatibility
            const baseUrl = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' 
                ? window.location.origin 
                : 'https://arztaleb.com';
            const packageUrl = `${baseUrl}#/package-info/${base64Data}`;
            
            // Update request with QR code and set status to InTransit
            await updateRequestStatus(requestId, RequestStatus.InTransit, qrString, workerId);

            return {
                requestId: requestId,
                qrCodeData: packageUrl
            };
        } catch (error) {
            console.error('Error creating manual package:', error);
            throw error;
        }
    };

    const addSellerPayment = async (payment: Omit<SellerPayment, 'id' | 'createdAt'>) => {
        try {
            await addDoc(collection(db, 'sellerPayments'), {
                ...payment,
                createdAt: Timestamp.now(),
                date: payment.date ? Timestamp.fromDate(new Date(payment.date)) : Timestamp.now()
            });
        } catch (error) {
            console.error('Error adding seller payment:', error);
            throw error;
        }
    };

    const createUserApprovalRequest = async (userId: string, officeId: string, message?: string, file?: File) => {
        try {
            // Check if user already has a pending request for this office
            const existingRequest = userApprovalRequests.find(
                req => req.userId === userId && 
                       req.officeId === officeId && 
                       req.status === UserApprovalRequestStatus.Pending &&
                       !req.archived
            );

            if (existingRequest) {
                throw new Error('You already have a pending approval request for this office');
            }

            let fileUrl: string | undefined;
            let fileName: string | undefined;

            // Upload file if provided
            if (file) {
                try {
                    // Create a unique file name
                    const timestamp = Date.now();
                    const fileExtension = file.name.split('.').pop();
                    const fileNameWithTimestamp = `approval-request-${userId}-${timestamp}.${fileExtension}`;
                    const storageRef = ref(storage, `userApprovalRequests/${fileNameWithTimestamp}`);
                    
                    // Upload file
                    await uploadBytes(storageRef, file);
                    
                    // Get download URL
                    fileUrl = await getDownloadURL(storageRef);
                    fileName = file.name;
                } catch (uploadError) {
                    console.error('Error uploading file:', uploadError);
                    throw new Error('Failed to upload file. Please try again.');
                }
            }

            const requestRef = await addDoc(collection(db, 'userApprovalRequests'), {
                userId,
                officeId,
                status: UserApprovalRequestStatus.Pending,
                message: message || '',
                fileUrl: fileUrl || '',
                fileName: fileName || '',
                createdAt: Timestamp.now()
            });
            
            // Send notification to office about new account approval request
            const user = users.find(u => u.id === userId);
            const currentLang = (localStorage.getItem('language') as 'ar' | 'tr') || 'tr';
            const title = currentLang === 'ar' ? 'طلب موافقة حساب جديد' : 'Yeni Hesap Onay Talebi';
            const notificationMessage = currentLang === 'ar'
                ? `طلب موافقة حساب جديد من ${user?.name || 'مستخدم'}`
                : `${user?.name || 'Kullanıcı'} tarafından yeni hesap onay talebi`;
            await sendNotificationToOffice(
                users,
                officeId,
                NotificationType.AccountApprovalRequest,
                title,
                notificationMessage,
                requestRef.id,
                { userId }
            );
        } catch (error) {
            console.error('Error creating user approval request:', error);
            throw error;
        }
    };

    const handleUserApprovalRequest = async (requestId: string, status: UserApprovalRequestStatus, reviewerId: string, adminNotes?: string) => {
        try {
            const requestRef = doc(db, 'userApprovalRequests', requestId);
            const request = userApprovalRequests.find(r => r.id === requestId);
            
            if (!request) {
                throw new Error('Approval request not found');
            }

            if (request.status !== UserApprovalRequestStatus.Pending) {
                throw new Error('This request has already been processed');
            }

            const now = Timestamp.now();

            // Update the approval request
            await updateDoc(requestRef, {
                status,
                reviewedAt: now,
                reviewedBy: reviewerId,
                adminNotes: adminNotes || ''
            });

            // If approved, update the user
            if (status === UserApprovalRequestStatus.Approved) {
                const userRef = doc(db, 'users', request.userId);
                await updateDoc(userRef, {
                    approved: true,
                    approvedAt: now,
                    approvedBy: reviewerId,
                    approvalRequestId: requestId
                });
            }
        } catch (error) {
            console.error('Error handling user approval request:', error);
            throw error;
        }
    };

    const handleLostPackageReport = async (reportId: string, status: LostPackageReportStatus, adminUserId: string, adminNotes?: string) => {
        try {
            const reportRef = doc(db, 'lostPackageReports', reportId);
            const report = lostPackageReports.find(r => r.id === reportId);
            
            if (!report) {
                throw new Error('Report not found');
            }

            const now = Timestamp.now();

            // Update the report
            await updateDoc(reportRef, {
                status,
                reviewedAt: now,
                reviewedBy: adminUserId,
                adminNotes: adminNotes || ''
            });

            // If approved, deduct the package value from user balance and mark request as canceled
            if (status === LostPackageReportStatus.Approved) {
                const request = requests.find(r => r.id === report.requestId);
                if (!request) {
                    throw new Error('Request not found');
                }

                const post = posts.find(p => p.id === request.postId);
                if (!post) {
                    throw new Error('Post not found');
                }

                // Update request status to Canceled
                await updateRequestStatus(report.requestId, RequestStatus.Canceled);

                // Calculate package value using maliyet if available, otherwise use pricePerKg
                const costPerKg = post.maliyet || post.pricePerKg || 0;
                const packageValue = request.requestedKg * costPerKg;

                // Deduct from user balance
                const userRef = doc(db, 'users', report.userId);
                await updateDoc(userRef, {
                    balance: increment(-packageValue)
                });

                // Add transaction record
                await addDoc(collection(db, 'transactions'), {
                    userId: report.userId,
                    amount: packageValue,
                    type: TransactionType.Withdrawal,
                    date: Timestamp.now(),
                    description: `Deduction for lost package ${request.packageId || `pkg-${report.requestId}`}`
                });
                
                // Check if post should be archived after lost package approval
                await checkAndArchivePostIfStockDepleted(post.id);
            }
        } catch (error) {
            console.error('Error handling lost package report:', error);
            throw error;
        }
    };

    const createWithdrawalRequest = async (userId: string, officeId: string, amount: number, currency?: string) => {
        try {
            if (amount <= 0) {
                throw new Error('Amount must be greater than 0');
            }

            const currentUser = users.find(u => u.id === userId);
            if (!currentUser) {
                throw new Error('User not found');
            }

            if (amount > currentUser.balance) {
                throw new Error('Insufficient balance');
            }

            const office = offices.find(o => o.id === officeId);
            if (!office) {
                throw new Error('Office not found');
            }

            // Check if there's already a pending request for this office
            const existingPending = withdrawalRequests.find(
                wr => wr.userId === userId && wr.officeId === officeId && wr.status === WithdrawalRequestStatus.Pending
            );
            if (existingPending) {
                throw new Error('You already have a pending withdrawal request for this office');
            }

            const now = Timestamp.now();
            await addDoc(collection(db, 'withdrawalRequests'), {
                userId,
                officeId,
                amount,
                ...(currency && { currency }),
                status: WithdrawalRequestStatus.Pending,
                createdAt: now
            });
        } catch (error) {
            console.error('Error creating withdrawal request:', error);
            throw error;
        }
    };

    const handleWithdrawalRequest = async (requestId: string, status: WithdrawalRequestStatus, adminUserId: string, adminNotes?: string) => {
        try {
            const requestRef = doc(db, 'withdrawalRequests', requestId);
            const withdrawalRequest = withdrawalRequests.find(r => r.id === requestId);
            
            if (!withdrawalRequest) {
                throw new Error('Withdrawal request not found');
            }

            const now = Timestamp.now();

            // Update the request
            await updateDoc(requestRef, {
                status,
                reviewedAt: now,
                reviewedBy: adminUserId,
                adminNotes: adminNotes || ''
            });

            // If approved, deduct the amount from user balance
            if (status === WithdrawalRequestStatus.Approved) {
                // If currency is stored on the request, honor it; otherwise fall back to legacy calculation
                let withdrawalCurrency: string | undefined = withdrawalRequest.currency;

                // Calculate user's balance per office and currency to determine which currency to use
                const userTransactions = transactions.filter(tx => tx.userId === withdrawalRequest.userId);
                const office = offices.find(o => o.id === withdrawalRequest.officeId);
                const officeName = office?.name || 'Unknown Office';
                
                if (!withdrawalCurrency) {
                    // LEGACY PATH: no currency stored on request, derive best guess per office
                    // Calculate balance for this specific office
                    let balanceUSD = 0;
                    let balanceTRY = 0;
                    
                    userTransactions.forEach(tx => {
                        // Extract package/request ID from transaction description
                        const extractPackageId = (description: string): { requestId: string | null, packageId: string | null } => {
                            const pkgMatch = description.match(/pkg-([a-zA-Z0-9_-]+)/i);
                            if (pkgMatch) {
                                return { requestId: pkgMatch[1], packageId: `pkg-${pkgMatch[1]}` };
                            }
                            const reqMatch = description.match(/REQ-([a-zA-Z0-9_-]+)/i);
                            if (reqMatch) {
                                return { requestId: reqMatch[1], packageId: null };
                            }
                            return { requestId: null, packageId: null };
                        };

                        const { requestId } = extractPackageId(tx.description);
                        
                        let officeId: string | null = null;
                        let currency: string = 'USD';

                        // Handle withdrawal transactions
                        if (tx.description.includes('Withdrawal from')) {
                            const officeMatch = tx.description.match(/Withdrawal from (.+?)(?: \[(USD|TRY)\])?$/);
                            if (officeMatch) {
                                const txOfficeName = officeMatch[1];
                                const txOffice = offices.find(o => o.name === txOfficeName);
                                if (txOffice && txOffice.id === withdrawalRequest.officeId) {
                                    officeId = txOffice.id;
                                    currency = officeMatch[2] || 'USD';
                                }
                            }
                        } else if (tx.description.includes('lost package') || tx.description.includes('Lost package') || tx.description.includes('Deduction for lost package')) {
                            if (requestId) {
                                const request = requests.find(r => r.id === requestId || r.packageId === `pkg-${requestId}`);
                                if (request) {
                                    const post = posts.find(p => p.id === request.postId);
                                    if (post && post.officeId === withdrawalRequest.officeId) {
                                        officeId = post.officeId;
                                        const postAny = post as any;
                                        let product = null;
                                        if (postAny?.productId) {
                                            product = products.find(p => p.id === postAny.productId);
                                        } else if (postAny?.sellerId) {
                                            product = products.find(p => 
                                                p.sellerId === postAny.sellerId &&
                                                p.fromCity === post.fromCity &&
                                                p.toCity === post.toCity &&
                                                p.fromCountry === post.fromCountry &&
                                                p.toCountry === post.toCountry
                                            );
                                        }
                                        currency = (post as any)?.maliyetCurrency || (product as any)?.maliyetCurrency || 'USD';
                                    }
                                }
                            }
                        } else if (requestId) {
                            const request = requests.find(r => r.id === requestId || r.packageId === `pkg-${requestId}`);
                            if (request) {
                                const post = posts.find(p => p.id === request.postId);
                                if (post && post.officeId === withdrawalRequest.officeId) {
                                    officeId = post.officeId;
                                    const postAny = post as any;
                                    let product = null;
                                    if (postAny?.productId) {
                                        product = products.find(p => p.id === postAny.productId);
                                    } else if (postAny?.sellerId) {
                                        product = products.find(p => 
                                            p.sellerId === postAny.sellerId &&
                                            p.fromCity === post.fromCity &&
                                            p.toCity === post.toCity &&
                                            p.fromCountry === post.fromCountry &&
                                            p.toCountry === post.toCountry
                                        );
                                    }
                                    currency = (post as any)?.pricePerKgCurrency || (product as any)?.pricePerKgCurrency || 'USD';
                                }
                            }
                        }

                        if (officeId === withdrawalRequest.officeId) {
                            if (tx.type === TransactionType.Credit) {
                                if (currency === 'TRY') {
                                    balanceTRY += tx.amount;
                                } else {
                                    balanceUSD += tx.amount;
                                }
                            } else {
                                if (currency === 'TRY') {
                                    balanceTRY -= tx.amount;
                                } else {
                                    balanceUSD -= tx.amount;
                                }
                            }
                        }
                    });

                    // Determine which currency to use for withdrawal
                    let derivedCurrency = 'USD';
                    if (balanceTRY >= withdrawalRequest.amount) {
                        derivedCurrency = 'TRY';
                    } else if (balanceUSD >= withdrawalRequest.amount) {
                        derivedCurrency = 'USD';
                    } else {
                        if (balanceTRY > 0 && balanceTRY >= balanceUSD) {
                            derivedCurrency = 'TRY';
                        } else {
                            derivedCurrency = 'USD';
                        }
                    }

                    withdrawalCurrency = derivedCurrency;
                }

                const userRef = doc(db, 'users', withdrawalRequest.userId);
                const user = users.find(u => u.id === withdrawalRequest.userId);
                const oldBalance = user?.balance || 0;
                await updateDoc(userRef, {
                    balance: increment(-withdrawalRequest.amount)
                });

                // Add transaction record with currency information
                await addDoc(collection(db, 'transactions'), {
                    userId: withdrawalRequest.userId,
                    amount: withdrawalRequest.amount,
                    type: TransactionType.Withdrawal,
                    date: Timestamp.now(),
                    description: `Withdrawal from ${officeName} [${withdrawalCurrency || 'USD'}]`
                });
                
                // Send balance notification
                if (user) {
                    const currentLang = (localStorage.getItem('language') as 'ar' | 'tr') || 'tr';
                    const title = currentLang === 'ar' ? 'تحديث الرصيد' : 'Bakiye Güncellendi';
                    const newBalance = oldBalance - withdrawalRequest.amount;
                    const message = currentLang === 'ar'
                        ? `تم سحب ${withdrawalRequest.amount.toFixed(2)} من رصيدك. الرصيد الجديد: ${newBalance.toFixed(2)}`
                        : `${withdrawalRequest.amount.toFixed(2)} bakiyenizden çekildi. Yeni bakiye: ${newBalance.toFixed(2)}`;
                    await createNotification(
                        withdrawalRequest.userId,
                        NotificationType.BalanceNotification,
                        title,
                        message,
                        requestId,
                        { amount: -withdrawalRequest.amount, newBalance }
                    );
                }
            }
        } catch (error) {
            console.error('Error handling withdrawal request:', error);
            throw error;
        }
    };

    const handleShortageDeliveryAction = async (shortageId: string, action: 'deduct' | 'hold', officeUserId: string) => {
        try {
            const shortageRef = doc(db, 'shortageDeliveries', shortageId);
            const shortage = shortageDeliveries.find(s => s.id === shortageId);
            
            if (!shortage) {
                throw new Error('Shortage delivery not found');
            }

            if (action === 'hold') {
                // Beklemeye al - teslimi kabul et, eksik miktarı bekle
                await updateDoc(shortageRef, {
                    status: ShortageDeliveryStatus.OnHold,
                    action: 'hold',
                    reviewedAt: Timestamp.now(),
                    reviewedBy: officeUserId
                });
            } else if (action === 'deduct') {
                // Bakiyeden düş
                const request = requests.find(r => r.id === shortage.requestId);
                const post = posts.find(p => p.id === shortage.postId);
                if (!post) {
                    throw new Error('Post not found');
                }
                
                const costPerKg = post.pricePerKg || 0;
                const deductionAmount = shortage.missingKg * costPerKg;
                
                // Kullanıcı bakiyesinden düş
                const userRef = doc(db, 'users', shortage.userId);
                await updateDoc(userRef, {
                    balance: increment(-deductionAmount)
                });
                
                // Transaction kaydı
                await addDoc(collection(db, 'transactions'), {
                    userId: shortage.userId,
                    amount: deductionAmount,
                    type: TransactionType.Withdrawal,
                    date: Timestamp.now(),
                    description: `Deduction for missing weight: ${shortage.missingKg} kg from package ${request?.packageId || shortage.requestId}`
                });
                
                await updateDoc(shortageRef, {
                    status: ShortageDeliveryStatus.BalanceDeducted,
                    action: 'deduct',
                    reviewedAt: Timestamp.now(),
                    reviewedBy: officeUserId
                });
            }
        } catch (error) {
            console.error('Error handling shortage delivery action:', error);
            throw error;
        }
    };

    const deliverMissingWeight = async (shortageId: string, deliveredKg: number, workerId?: string) => {
        try {
            const shortageRef = doc(db, 'shortageDeliveries', shortageId);
            const shortage = shortageDeliveries.find(s => s.id === shortageId);
            
            if (!shortage) {
                throw new Error('Shortage delivery not found');
            }
            
            if (shortage.status !== ShortageDeliveryStatus.OnHold) {
                throw new Error('Can only deliver missing weight for on-hold shortages');
            }
            
            // Calculate total delivered amount
            const existingDelivered = shortage.missingKgDelivered || 0;
            const totalDelivered = existingDelivered + deliveredKg;
            
            // Update the delivered amount
            await updateDoc(shortageRef, {
                missingKgDelivered: totalDelivered,
                missingKgDeliveredAt: Timestamp.now()
            });
            
            // Eğer tüm eksik miktar getirildiyse tamamla
            if (totalDelivered >= shortage.missingKg) {
                await updateDoc(shortageRef, {
                    status: ShortageDeliveryStatus.Completed
                });
                
                // Kullanıcıya kalan kredi ekle (sadece yeni teslim edilen kısım için)
                const request = requests.find(r => r.id === shortage.requestId);
                const post = posts.find(p => p.id === shortage.postId);
                if (!post) {
                    throw new Error('Post not found');
                }
                
                const costPerKg = post.pricePerKg || 0;
                const creditAmount = deliveredKg * costPerKg;
                
                if (creditAmount > 0) {
                    const userRef = doc(db, 'users', shortage.userId);
                    await updateDoc(userRef, {
                        balance: increment(creditAmount)
                    });
                    
                    await addDoc(collection(db, 'transactions'), {
                        userId: shortage.userId,
                        amount: creditAmount,
                        type: TransactionType.Credit,
                        date: Timestamp.now(),
                        description: `Credit for delivered missing weight: ${deliveredKg} kg for package ${request?.packageId || shortage.requestId}`
                    });
                }
            } else {
                // Partial delivery - credit for the delivered portion
                const request = requests.find(r => r.id === shortage.requestId);
                const post = posts.find(p => p.id === shortage.postId);
                if (!post) {
                    throw new Error('Post not found');
                }
                
                const costPerKg = post.pricePerKg || 0;
                const creditAmount = deliveredKg * costPerKg;
                
                if (creditAmount > 0) {
                    const userRef = doc(db, 'users', shortage.userId);
                    await updateDoc(userRef, {
                        balance: increment(creditAmount)
                    });
                    
                    await addDoc(collection(db, 'transactions'), {
                        userId: shortage.userId,
                        amount: creditAmount,
                        type: TransactionType.Credit,
                        date: Timestamp.now(),
                        description: `Credit for partial missing weight delivery: ${deliveredKg} kg for package ${request?.packageId || shortage.requestId}`
                    });
                }
            }
        } catch (error) {
            console.error('Error delivering missing weight:', error);
            throw error;
        }
    };

    const markNotificationAsRead = async (notificationId: string) => {
        try {
            const notificationRef = doc(db, 'notifications', notificationId);
            await updateDoc(notificationRef, {
                read: true,
                readAt: Timestamp.now()
            });
        } catch (error) {
            console.error('Error marking notification as read:', error);
            throw error;
        }
    };

    const handleShortageFinalAction = async (shortageId: string, action: 'deduct' | 'pay', officeUserId: string, userApprovalId?: string) => {
        try {
            const shortageRef = doc(db, 'shortageDeliveries', shortageId);
            const shortage = shortageDeliveries.find(s => s.id === shortageId);
            
            if (!shortage) {
                throw new Error('Shortage delivery not found');
            }
            
            if (shortage.status !== ShortageDeliveryStatus.OnHold) {
                throw new Error('Can only perform final action on on-hold shortages');
            }
            
            const request = requests.find(r => r.id === shortage.requestId);
            const post = posts.find(p => p.id === shortage.postId);
            if (!post) {
                throw new Error('Post not found');
            }
            
            const costPerKg = post.pricePerKg || 0;
            const remainingMissingKg = shortage.missingKg - (shortage.missingKgDelivered || 0);
            const amount = remainingMissingKg * costPerKg;
            
            if (action === 'deduct') {
                // Bakiyeden düş (kullanıcı onayı gerekebilir)
                if (amount > 0) {
                    const userRef = doc(db, 'users', shortage.userId);
                    await updateDoc(userRef, {
                        balance: increment(-amount)
                    });
                    
                    await addDoc(collection(db, 'transactions'), {
                        userId: shortage.userId,
                        amount: amount,
                        type: TransactionType.Withdrawal,
                        date: Timestamp.now(),
                        description: `Deduction for undelivered missing weight: ${remainingMissingKg} kg from package ${request?.packageId || shortage.requestId}`
                    });
                }
                
                await updateDoc(shortageRef, {
                    status: ShortageDeliveryStatus.BalanceDeducted,
                    finalAction: 'deduct',
                    finalActionApprovedBy: userApprovalId || officeUserId,
                    finalActionApprovedAt: Timestamp.now()
                });
            } else if (action === 'pay') {
                // Peşin ödeme - kullanıcıdan al (karşılıklı onay gerekli)
                await updateDoc(shortageRef, {
                    status: ShortageDeliveryStatus.PaidUpfront,
                    finalAction: 'pay',
                    finalActionApprovedBy: userApprovalId || officeUserId,
                    finalActionApprovedAt: Timestamp.now()
                });
            }
        } catch (error) {
            console.error('Error handling shortage final action:', error);
            throw error;
        }
    };

    return (
        <DataContext.Provider value={{ 
            users, 
            setUsers, 
            posts, 
            setPosts, 
            requests, 
            setRequests, 
            transactions, 
            setTransactions, 
            sellers,
            setSellers,
            products,
            setProducts,
            sellerPayments,
            setSellerPayments,
            lostPackageReports,
            setLostPackageReports,
            offices,
            setOffices,
            withdrawalRequests,
            setWithdrawalRequests,
            pendingTransfers,
            setPendingTransfers,
            userApprovalRequests,
            setUserApprovalRequests,
            shortageDeliveries,
            setShortageDeliveries,
            notifications,
            setNotifications,
            loading,
            addRequest, 
            updateRequestStatus, 
            addPost, 
            updatePost, 
            markPackageDelivered,
            addSeller,
            updateSeller,
            deleteSeller,
            addProduct,
            updateProduct,
            deleteProduct,
            deletePost,
            checkAndArchiveAllPosts,
            transferPackage,
            reportLostPackage,
            handleLostPackageReport,
            // Withdrawal Requests
            createWithdrawalRequest,
            handleWithdrawalRequest,
            // Pending Transfers
            createPendingTransfer,
            confirmPendingTransfer,
            cancelPendingTransfer,
            // Office and Worker Management
            addOffice,
            updateOffice,
            deleteOffice,
            addUser,
            updateUser,
            deleteUser,
            addSellerPayment,
            // User Approval Requests
            createUserApprovalRequest,
            handleUserApprovalRequest,
            // Shortage Delivery Management
            handleShortageDeliveryAction,
            deliverMissingWeight,
            handleShortageFinalAction,
            // Manual Package Creation
            createManualPackage,
            // Notifications
            markNotificationAsRead
        }}>
            {children}
        </DataContext.Provider>
    );
};

export const useData = (): DataContextType => {
    const context = useContext(DataContext);
    if (!context) {
        throw new Error('useData must be used within a DataProvider');
    }
    return context;
};
